import {
  AfterContentInit,
  AfterViewInit,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NgProgressService } from '@shared/services/ng-progress.service';
import { CartService } from '../cart.service';
import { CartItem } from '../cart.model';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { FluidModule } from 'primeng/fluid';
import { Router } from '@angular/router';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { CardModule } from 'primeng/card';
import { SkeletonModule } from 'primeng/skeleton';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberInputEvent, InputNumberModule } from 'primeng/inputnumber';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { GlobalProgressSpinnerService } from '@shared/services/global-progress-spinner.service';
import { CheckboxModule } from 'primeng/checkbox';
import { WalletService } from '../../wallet/wallet.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { MenuItem, MessageService } from 'primeng/api';
import { CustomCurrencyPipe } from '@shared/pipes/custom-currency.pipe';
import { ProfileService } from '../../profile/profile.service';

@Component({
  selector: 'app-payment',
  imports: [
    CommonModule,
    ButtonModule,
    FluidModule,
    AngularSvgIconModule,
    CardModule,
    SkeletonModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    ReactiveFormsModule,
    DialogModule,
    CheckboxModule,
    FormsModule,
    BreadcrumbComponent,
    CustomCurrencyPipe,
  ],
  templateUrl: './payment.component.html',
})
export class PaymentComponent implements OnInit, AfterViewInit, OnDestroy {
  form!: FormGroup;
  cart: CartItem[] = [];
  isLoading: boolean = true;
  totalWalletBalance: number = 0;
  totalPaymentAmount: number = 0;
  showPaymentTermsDialog: boolean = false;
  confirmTerms: boolean = false;
  currency: string | undefined;
  readonly breadcrumdItems: MenuItem[] = [
    {
      label: 'Cart',
      routerLink: '/home/cart',
    },
    {
      label: 'Payment method',
    },
  ];

  private cartSub: Subscription = Subscription.EMPTY;
  private walletSub: Subscription = Subscription.EMPTY;

  readonly _globalProgressSpinnerService = inject(GlobalProgressSpinnerService);
  private readonly _ngProgressService = inject(NgProgressService);
  private readonly _cartService = inject(CartService);
  private readonly _router = inject(Router);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _walletService = inject(WalletService);
  private readonly _profileService = inject(ProfileService);
  private readonly _messageService = inject(MessageService);

  get amounts(): FormArray {
    return this.form.get('amounts') as FormArray;
  }

  ngOnInit(): void {
    this._ngProgressService.startLoader();

    this.currency = this._profileService.profile.currency;

    this.walletSub = this._walletService.wallet$.subscribe(
      (wallet) =>
        (this.totalWalletBalance = wallet.cashBalance + wallet.rewardsBalance)
    );

    this.cartSub = this._cartService.cart$.subscribe((cart) => {
      this.cart = cart;

      // [Todo] Remove setTimeout latter
      setTimeout(() => {
        this.isLoading = false;
      }, 200);

      this.form = this._formBuilder.group({
        amounts: this._formBuilder.array([]),
      });

      this.cart.forEach((item) => this.addItemControl(item.amount));

      this.onUpdateTotalPaymentAmount();
    });

    this._cartService.getCart();
    this._walletService.getWallet();
  }

  ngAfterViewInit(): void {
    this._ngProgressService.completeLoader();
  }

  onViewProperties(): void {
    this._router.navigate(['/home/properties']);
  }

  onUpdateTotalPaymentAmount(): void {
    this.totalPaymentAmount = this.amounts.controls
      .map((control) => control.value || 0)
      .reduce((sum, value) => sum + value, 0);
  }

  onInvest(): void {
    const cart = [...this.cart];
    this._walletService.onInvest(cart).then(
      (response) => {
        this._cartService.clearCart();
        this._cartService.setInvestedItems(cart);
        this._router.navigate(['home/cart/payment/investment-confirmed']);

        this._globalProgressSpinnerService.hideLoader();
      },
      (error) => {
        console.log('Failure', error); // Failure

        this._messageService.add({
          severity: 'error',
          summary: error,
        });

        this._globalProgressSpinnerService.hideLoader();
      }
    );
  }

  onAddMoney(): void {
    this._router.navigate(['home/wallet/deposit']);
  }

  private addItemControl(amount: number): void {
    const control = this._formBuilder.control(
      { value: amount, disabled: true },
      [Validators.required, Validators.min(0.01)] // required and > 0
    );
    this.amounts.push(control);
  }

  ngOnDestroy(): void {
    this.cartSub.unsubscribe();
    this.walletSub.unsubscribe();
  }
}
